import { graphql, useStaticQuery } from 'gatsby';

import React from 'react';
import { PropTypes } from 'prop-types';
import Img from '../../../../../components/Img';
import { useIntl } from 'react-intl';

import Text from '../../../Text';
import { DynamicContainer } from '../../../Layout/DynamicContainer';

import { HoverConsumer } from '../../../../custom-cursor';

import quotes from '../../../../../../static/images/quotes.svg';

const TestimonialSlide = ({
    styles,
    rules,
    src,
    alt,
    name,
    testimonial,
    company,
    logo,
    link,
    context,
    setContext,
    showClutch,
}) => {
    const intl = useIntl();
    const { formatMessage } = intl;
    const altMessage = formatMessage({ id: alt });
    const nameMessage = formatMessage({ id: name });
    const companyMessage = formatMessage({ id: company });
    const testimonialMessage = formatMessage({ id: testimonial });
    const quotesMessage = formatMessage({ id: 'imageAlts.quotesIcon' });
    const localizedLink = formatMessage({ id: link });

    const images = useStaticQuery(graphql`
        query {
            allFile(filter: { relativeDirectory: { eq: "people" } }) {
                nodes {
                    base
                    relativePath
                    childImageSharp {
                        fluid(quality: 90, cropFocus: SOUTHWEST, maxWidth: 180, maxHeight: 180) {
                            ...GatsbyImageSharpFluid_withWebp_tracedSVG
                        }
                    }
                }
            }
        }
    `);

    const match = React.useMemo(
        () => images.allFile.nodes.find(({ relativePath }) => src === relativePath),
        [images, src],
    );

    const {
        childImageSharp: { fluid },
    } = match;

    const handleLeave = () => {
        setContext({
            ...context,
            carousel: {
                ...context.carousel,
                titleHovered: false,
            },
        });
    };

    const handleEnter = () => {
        setContext({
            ...context,
            carousel: {
                ...context.carousel,
                titleHovered: true,
                titleMessageId: 'aboutUsPage.testimonials.visitTestimonial.tooltip',
            },
        });
    };

    return (
        <div className={styles.container}>
            <DynamicContainer extend={rules.extendContainer} isFluid>
                <div className={styles.testimonialContainer}>
                    <img alt={quotesMessage} src={quotes} className={styles.quotes} />
                    {showClutch ? (
                        // eslint-disable-next-line
                        <a
                            target="_blank"
                            className={styles.link}
                            href={localizedLink}
                            onMouseEnter={handleEnter}
                            onMouseLeave={handleLeave}
                        >
                            <Text extend={rules.extendLinkTestimonial}>{testimonialMessage}</Text>
                        </a>
                    ) : (
                        <Text extend={rules.extendTestimonial}>{testimonialMessage}</Text>
                    )}
                </div>
                <div className={styles.authorContainer}>
                    <div className={styles.authorImage}>
                        <Img alt={altMessage} fluid={fluid} />
                    </div>
                    <div>
                        <Text extend={rules.extendText}>{nameMessage}</Text>
                        <Text extend={rules.extendCompanyText}>{companyMessage}</Text>
                        {logo && <img className={styles.logo} src={logo} alt="" />}
                    </div>
                </div>
            </DynamicContainer>
        </div>
    );
};

TestimonialSlide.propTypes = {
    styles: PropTypes.shape({
        container: PropTypes.string.isRequired,
        testimonialContainer: PropTypes.string.isRequired,
        authorContainer: PropTypes.string.isRequired,
        authorImage: PropTypes.string.isRequired,
        logo: PropTypes.string.isRequired,
        quotes: PropTypes.string.isRequired,
    }).isRequired,
    rules: PropTypes.shape().isRequired,
    src: PropTypes.string,
    alt: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    testimonial: PropTypes.string.isRequired,
    company: PropTypes.string.isRequired,
    logo: PropTypes.string,
    link: PropTypes.string,

    context: PropTypes.shape().isRequired,
    setContext: PropTypes.func.isRequired,
};

TestimonialSlide.defaultProps = {
    link: '#',
    src: null,
    logo: null,
};

const TestimonialSlideWrapper = props => (
    <HoverConsumer>
        {render => <TestimonialSlide context={render.context} setContext={render.setContext} {...props} />}
    </HoverConsumer>
);

export default TestimonialSlideWrapper;
